import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, RefreshCw, Eye,
  AlertCircle, CheckCircle, Activity, PieChart, CreditCard,
  Wallet, ShoppingCart, Settings, Key, ArrowRight,
  CheckSquare, Square, MinusSquare, ChevronDown, ChevronUp,
  Globe, Landmark, Coins
} from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { tradingService, handleAPIError } from '../services/api';
import { formatCurrency, formatPercent, getChangeColor } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';

/* ─── 포맷 ─── */
const krwFmt = (v) => new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(v ?? 0);
const usdFmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v ?? 0);
const pctFmt = (v) => `${(v ?? 0) >= 0 ? '+' : ''}${Number(v ?? 0).toFixed(2)}%`;
const pColor = (v) => Number(v) > 0 ? 'text-green-600' : Number(v) < 0 ? 'text-red-600' : 'text-gray-700';
const pBg   = (v) => Number(v) > 0 ? 'text-green-400' : Number(v) < 0 ? 'text-red-300'  : 'text-white/60';

/* ─── 접이식 패널: 헤더(요약)는 항상 보이고, body만 접힘 ─── */
const CollapsiblePanel = ({ header, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* 헤더 클릭 → body 토글 */}
      <div
        className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors select-none"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex-1">{header}</div>
        <div className="ml-4 shrink-0 text-gray-400">
          {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
};

/* ─── 자동매매 투자한도 사용 현황 카드 (2026-10-06) ───
   2026-10-06: 한도는 '자동매매 보유분' 기준. 개인 보유분(앱 수동/MTS/44)은 따로 표시 */
/* 미국(USD) 한도 한 줄 요약 */
const UsUsageRow = ({ u }) => {
  if (!u) return null;
  if (u.error) {
    return <p className="mt-4 pt-3 border-t border-gray-100 text-xs text-yellow-700">미국 투자한도 현황 조회 실패: {u.error}</p>;
  }
  const limit = u.limit_usd || 0;
  const pct = limit > 0 ? (u.invested_usd / limit) * 100 : 0;
  const over = u.over_limit_usd > 0;
  const bar = u.has_margin_debt || over ? 'bg-red-500' : pct >= 90 ? 'bg-amber-500' : 'bg-purple-500';
  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
        <span className="flex items-center font-medium text-gray-800">
          <Globe className="w-4 h-4 mr-1.5 text-purple-600" />미국 주식
          {!u.auto_trading_enabled && <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">자동매매 꺼짐</span>}
        </span>
        <span className="text-gray-600">
          한도 <b>{limit > 0 ? usdFmt(limit) : '미설정'}</b> · 자동매매 투입 <b className={over ? 'text-red-600' : ''}>{usdFmt(u.invested_usd)}</b>
          {' · '}{over ? <span className="text-red-600">초과 {usdFmt(u.over_limit_usd)}</span> : <>잔여 {usdFmt(u.remaining_limit_usd)}</>}
          {' · '}자동매수 가능 <b className="text-blue-700">{usdFmt(u.buy_budget_usd)}</b>
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mt-2">
        <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>주문가능 {usdFmt(u.available_order_usd)} · 외화예수금 {usdFmt(u.usd_deposit)}{u.manual_holdings?.length ? ` · 개인 보유 ${usdFmt(u.manual_invested_usd)} (${u.manual_holdings.length}종목)` : ''}</span>
        <span>{limit > 0 ? `${pct.toFixed(1)}%` : '-'}</span>
      </div>
      {(u.has_margin_debt || u.buy_blocked_reason) && (
        <p className="text-xs text-red-500 mt-1">{u.has_margin_debt ? '외화 미수 발생 - ' : ''}{u.buy_blocked_reason || ''} → 미국 자동매수 중단</p>
      )}
    </div>
  );
};

const InvestmentUsageCard = ({ usage, usUsage, error, showUntracked, setShowUntracked, onSettings }) => {
  if (!usage) {
    return error ? (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center text-sm text-yellow-800">
        <AlertCircle className="h-4 w-4 mr-2" />투자한도 현황을 불러오지 못했습니다: {error}
      </div>
    ) : null;
  }
  const limit = usage.limit_krw || 0;
  const invested = usage.invested_krw || 0;
  const pct = limit > 0 ? (invested / limit) * 100 : 0;
  const over = usage.over_limit_krw > 0;
  const barColor = usage.has_margin_debt || over ? 'bg-red-500' : pct >= 90 ? 'bg-amber-500' : 'bg-emerald-500';
  const untracked = usage.manual_holdings || [];
  return (
    <div className={`bg-white rounded-xl shadow-sm border p-5 ${usage.has_margin_debt || over ? 'border-red-300' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="flex items-center space-x-2">
          <Coins className="w-5 h-5 text-emerald-600" />
          <span className="font-semibold text-gray-900">자동매매 투자한도 사용 현황</span>
          {!usage.auto_trading_enabled && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">자동매매 꺼짐</span>}
        </div>
        <button onClick={onSettings} className="text-xs text-blue-600 hover:underline flex items-center">
          <Settings className="w-3.5 h-3.5 mr-1" />한도 변경
        </button>
      </div>

      {(usage.has_margin_debt || over) && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 flex items-start">
          <AlertCircle className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
          <div>
            {usage.has_margin_debt && <p>미수 발생: D+2 예수금 {krwFmt(usage.d2_deposit_krw)}. 자동매수가 중단됩니다.</p>}
            {over && <p>설정 한도를 {krwFmt(usage.over_limit_krw)} 초과했습니다. 자동매수가 중단됩니다.</p>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <div>
          <p className="text-xs text-gray-400">국내 설정 투자한도</p>
          <p className="text-lg font-bold text-gray-900">{limit > 0 ? krwFmt(limit) : '미설정'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">자동매매 투입 (매입금액)</p>
          <p className={`text-lg font-bold ${over ? 'text-red-600' : 'text-gray-900'}`}>{krwFmt(invested)}</p>
          <p className="text-xs text-gray-400">평가 {krwFmt(usage.evaluated_krw)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">{over ? '초과 금액' : '한도 잔여'}</p>
          <p className={`text-lg font-bold ${over ? 'text-red-600' : 'text-emerald-600'}`}>
            {krwFmt(over ? usage.over_limit_krw : usage.remaining_limit_krw)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400">지금 자동매수 가능</p>
          <p className="text-lg font-bold text-blue-700">{krwFmt(usage.buy_budget_krw)}</p>
          {usage.buy_blocked_reason && <p className="text-xs text-red-500">{usage.buy_blocked_reason}</p>}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>사용률</span><span>{limit > 0 ? `${pct.toFixed(1)}%` : '-'}</span>
        </div>
        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
        <span>자동매매 {usage.auto_holdings_count}종목</span>
        <span>계좌 전체 매입 {krwFmt(usage.account_invested_krw)} ({usage.account_holdings_count}종목)</span>
        {untracked.length > 0 && (
          <button className="text-amber-600 hover:underline" onClick={() => setShowUntracked(v => !v)}>
            개인 보유 {krwFmt(usage.manual_invested_krw)} ({untracked.length}종목) {showUntracked ? '▲' : '▼'}
          </button>
        )}
      </div>

      {showUntracked && untracked.length > 0 && (
        <div className="mt-2 border border-amber-100 bg-amber-50 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="text-amber-800">
              <tr><th className="text-left px-3 py-1.5">종목</th><th className="text-right px-3">계좌 수량</th>
                  <th className="text-right px-3">자동매매</th><th className="text-right px-3">개인 보유 금액</th></tr>
            </thead>
            <tbody>
              {untracked.map(u => (
                <tr key={u.stock_code} className="border-t border-amber-100">
                  <td className="px-3 py-1.5">{u.stock_name} <span className="text-gray-400">{u.stock_code}</span></td>
                  <td className="text-right px-3">{u.account_qty.toLocaleString()}</td>
                  <td className="text-right px-3">{Number(u.auto_qty).toLocaleString()}</td>
                  <td className="text-right px-3">{krwFmt(u.manual_amount_krw)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-1.5 text-[11px] text-amber-700">앱에서 직접 산 것·MTS·44번 앱 등 개인 보유분입니다. 자동매매 한도 계산에서 빠지고, 자동매매가 팔지 않습니다. 개인이 팔면 개인 물량부터 차감됩니다.</p>
        </div>
      )}

      {(usage.reconcile?.actions || []).length > 0 && (
        <div className="mt-3 text-xs bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-blue-800">
          {usage.reconcile.actions.map((a, i) => (
            <p key={i}>📒 {a.stock_code} {
              a.action === 'MANUAL_BUY' ? '개인 매수 반영'
              : a.action === 'SELL' ? (a.book === 'AUTO' ? '개인 매도(자동매매 물량) → 실적 기록' : '개인 매도 → 개별매매 실적 기록')
              : '수량 보정'} {Math.abs(a.qty)}주 — {a.note}</p>
          ))}
        </div>
      )}
      {usage.reconcile?.initialized && (
        <p className="mt-2 text-xs text-emerald-700">📒 실적 기준선 설정 완료: 지금 보유 종목을 자동매매/개인 물량으로 나눠 등록했습니다. 이후 매도부터 실적에 잡힙니다.</p>
      )}
      {usage.reconcile?.error && (
        <p className="mt-2 text-xs text-yellow-700">자동매매 장부 대사 실패(다음 조회 때 재시도): {usage.reconcile.error}</p>
      )}

      <UsUsageRow u={usUsage} />
    </div>
  );
};

const AccountPage = () => {
  const navigate = useNavigate();
  const [accountData,   setAccountData]   = useState(null);
  const [holdings,      setHoldings]      = useState([]);
  const [totalAsset,    setTotalAsset]    = useState(null);
  const [usHoldings,    setUsHoldings]    = useState([]);
  const [isLoading,     setIsLoading]     = useState(true);
  const [isRefreshing,  setIsRefreshing]  = useState(false);
  const [isBulkSelling, setIsBulkSelling] = useState(false);
  const [error,         setError]         = useState('');
  const [lastUpdated,   setLastUpdated]   = useState(null);
  const [selectedStocks, setSelectedStocks] = useState(new Set());
  const [usage,         setUsage]         = useState(null);   // 투자한도 사용 현황
  const [usageError,    setUsageError]    = useState('');
  const [showUntracked, setShowUntracked] = useState(false);
  const [usUsage,       setUsUsage]       = useState(null);   // 미국 투자한도 사용 현황

  const tkn = () => localStorage.getItem('token');

  const isAllSelected     = holdings.length > 0 && selectedStocks.size === holdings.length;
  const isPartialSelected = selectedStocks.size > 0 && selectedStocks.size < holdings.length;
  const handleSelectAll   = () => setSelectedStocks(isAllSelected ? new Set() : new Set(holdings.map(h => h.stock_code)));
  const handleSelectStock = (code) => {
    const s = new Set(selectedStocks);
    s.has(code) ? s.delete(code) : s.add(code);
    setSelectedStocks(s);
  };

  const loadAccountData = useCallback(async (refresh = false) => {
    refresh ? setIsRefreshing(true) : setIsLoading(true);
    setError('');
    try {
      const res = await tradingService.getAccountBalance();
      if (res.success) {
        setAccountData(res.data);
        setHoldings(res.data.holdings || []);
        setSelectedStocks(new Set());
        setLastUpdated(new Date());
      } else {
        throw new Error(res.error || '계좌 정보 조회 실패');
      }
    } catch (e) {
      setError(handleAPIError(e, '계좌 정보를 불러오는데 실패했습니다.').message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
    try {
      const r = await fetch('/trading-api/account/investment-usage', { headers: { Authorization: `Bearer ${tkn()}` } });
      const u = await r.json();
      if (u.success) { setUsage(u.data); setUsageError(''); }
      else setUsageError(u.detail || '투자한도 현황 조회 실패');
    } catch (e) { setUsageError('투자한도 현황 조회 실패'); }
    try {
      const r = await fetch('/trading-api/us/trade/investment-usage', { headers: { Authorization: `Bearer ${tkn()}` } });
      const u = await r.json();
      setUsUsage(u.success ? u.data : { error: u.detail || '조회 실패' });
    } catch (e) { setUsUsage({ error: '조회 실패' }); }
    try {
      const r = await fetch('/trading-api/account/total-asset', { headers: { Authorization: `Bearer ${tkn()}` } });
      const d = await r.json();
      if (d.success) setTotalAsset(d.data);
    } catch (e) { console.warn('총자산 조회 실패:', e); }
    try {
      const r = await fetch('/trading-api/us/trade/holdings', { headers: { Authorization: `Bearer ${tkn()}` } });
      const d = await r.json();
      if (d.success && Array.isArray(d.holdings)) {
        // UsPortfolioPage와 동일하게 종목별 실시간 현재가 조회
        const enriched = await Promise.all(
          d.holdings.map(async (h) => {
            try {
              const p = await fetch(
                `/trading-api/us/trade/price/${h.stock_code}?exchange=${h.exchange || 'NAS'}`,
                { headers: { Authorization: `Bearer ${tkn()}` } }
              ).then(r => r.json());
              if (p?.success && p.current_price) {
                const avg  = parseFloat(h.avg_buy_price)  || 0;
                const curr = parseFloat(p.current_price);
                const qty  = parseFloat(h.total_quantity) || 0;
                return { ...h, current_price: curr,
                          pnl_amount: (curr - avg) * qty,
                          pnl_pct: avg > 0 ? ((curr - avg) / avg) * 100 : 0 };
              }
            } catch { /* 현재가 조회 실패 시 원본 유지 */ }
            return h;
          })
        );
        setUsHoldings(enriched);
      }
    } catch (e) { console.warn('미국주식 조회 실패:', e); }
  }, []);

  useEffect(() => { loadAccountData(); }, []);

  const handleSellSelected = async () => {
    if (!selectedStocks.size) { alert('매도할 종목을 선택해주세요.'); return; }
    const list = holdings.filter(h => selectedStocks.has(h.stock_code)).map(h => `${h.stock_name}(${h.stock_code})`).join('\n');
    if (!window.confirm(`⚠️ 선택 ${selectedStocks.size}개 종목을 매도하시겠습니까?\n\n${list}\n\n이 작업은 취소할 수 없습니다.`)) return;
    try {
      setIsBulkSelling(true);
      const r = await fetch('/analysis-api/trading/bulk-sell', {
        method: 'POST', headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock_codes: Array.from(selectedStocks) }),
      });
      const d = await r.json();
      d.success ? alert(`✅ 선택 매도 완료!\n실행: ${d.executed_count || 0}개`) : alert(`❌ 매도 실패\n${d.message || '오류'}`);
      setSelectedStocks(new Set()); loadAccountData(true);
    } catch { alert('❌ 매도 중 오류가 발생했습니다'); }
    finally { setIsBulkSelling(false); }
  };

  const handleBulkSellAll = async () => {
    if (!window.confirm(`⚠️ 전체 ${holdings.length}개 종목을 일괄 매도하시겠습니까?\n이 작업은 취소할 수 없습니다.`)) return;
    try {
      setIsBulkSelling(true);
      const r = await fetch('/analysis-api/trading/bulk-sell', {
        method: 'POST', headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock_codes: null }),
      });
      const d = await r.json();
      d.success ? alert(`✅ 전체 매도 완료!\n실행: ${d.executed_count || 0}개`) : alert(`❌ 일괄 매도 실패\n${d.message || '오류'}`);
      setSelectedStocks(new Set()); loadAccountData(true);
    } catch { alert('❌ 일괄 매도 중 오류가 발생했습니다'); }
    finally { setIsBulkSelling(false); }
  };

  /* 미국 주식 집계 — pnl_amount는 실시간 현재가 기반 */
  const usTotalValue = usHoldings.reduce((s, h) => s + (parseFloat(h.current_price) || parseFloat(h.avg_buy_price)) * parseFloat(h.total_quantity), 0);
  const usTotalCost  = usHoldings.reduce((s, h) => s + parseFloat(h.avg_buy_price) * parseFloat(h.total_quantity), 0);
  const usTotalPL    = usHoldings.reduce((s, h) => s + (h.pnl_amount ?? ((parseFloat(h.current_price) || parseFloat(h.avg_buy_price)) - parseFloat(h.avg_buy_price)) * parseFloat(h.total_quantity)), 0);

  /* 국내 주식 집계 */
  const krTotalValue  = parseFloat(accountData?.total_stock_value || 0);
  const krPurchase    = parseFloat(accountData?.purchase_amount   || 0);
  const krPL          = parseFloat(accountData?.total_profit_loss || 0);
  const krPLRate      = krPurchase > 0 ? (krPL / krPurchase) * 100 : 0;
  const totalAssetKrw = totalAsset?.total_asset_krw || parseFloat(accountData?.total_asset || 0);

  if (isLoading) return (
    <Layout><div className="flex justify-center py-12"><Loading size="large" message="계좌 정보를 불러오는 중..." /></div></Layout>
  );

  if (error && !accountData) return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {error.includes('API keys') || error.includes('404') ? (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">API 키 설정이 필요합니다</h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <button onClick={() => navigate('/settings')} className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                <Settings className="w-5 h-5 mr-2" />설정 페이지로 이동<ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <button onClick={() => loadAccountData()} className="flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-4 h-4 mr-2" />다시 시도
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">계좌 조회 실패</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <button onClick={() => loadAccountData()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">다시 시도</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );

  const d = accountData || {};

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* ── 헤더 ── */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">계좌 현황</h1>
            <p className="text-gray-600 mt-1">실시간 계좌 정보 및 보유 종목</p>
            {lastUpdated && <p className="text-sm text-gray-500 mt-1">마지막 업데이트: {lastUpdated.toLocaleTimeString()}</p>}
          </div>
          <button onClick={() => loadAccountData(true)} disabled={isRefreshing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? '새로고침 중...' : '새로고침'}
          </button>
        </div>

        {error && accountData && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" /><p className="text-yellow-800">{error}</p>
          </div>
        )}

        {accountData && (
          <>
            {/* ══ 1. 상단 자산 카드 3칸 ══
                [한투 총 자산] [국내 주식 가치 + 평가손익] [미국 주식 자산]
            */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* 카드 1: 한투 총 자산 */}
              <div className="rounded-xl shadow-sm text-white p-5 bg-gradient-to-r from-indigo-600 to-indigo-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-xs font-medium">한투 총 자산 (국내+해외)</p>
                    <p className="text-2xl font-bold mt-1 leading-tight truncate">{krwFmt(totalAssetKrw)}</p>
                    <p className="text-white/70 text-xs mt-1">
                      국내 {krwFmt(totalAsset?.domestic_stock_value ?? krTotalValue)} | 해외 {krwFmt(totalAsset?.overseas_stock_value ?? 0)}
                    </p>
                  </div>
                  <div className="h-11 w-11 bg-white/20 rounded-xl flex items-center justify-center shrink-0 ml-3">
                    <Landmark className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>

              {/* 카드 2: 국내 주식 — 가치 + 평가손익 한 칸에 */}
              <div className={`rounded-xl shadow-sm text-white p-5 ${krPL >= 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white/80 text-xs font-medium">국내 주식</p>
                  <div className="h-9 w-9 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                    <PieChart className="h-5 w-5 text-white" />
                  </div>
                </div>
                {/* 가치 + 매입 */}
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-white/70 text-xs">평가금액</p>
                    <p className="text-xl font-bold leading-tight">{krwFmt(krTotalValue)}</p>
                    <p className="text-white/60 text-xs mt-0.5">매입 {krwFmt(krPurchase)}</p>
                  </div>
                  {/* 구분선 */}
                  <div className="w-px h-10 bg-white/20 mx-3" />
                  {/* 손익 */}
                  <div className="text-right">
                    <p className="text-white/70 text-xs">평가손익</p>
                    <p className={`text-xl font-bold leading-tight ${pBg(krPL)}`}>{krwFmt(krPL)}</p>
                    <p className={`text-xs mt-0.5 ${pBg(krPL)}`}>{pctFmt(krPLRate)}</p>
                  </div>
                </div>
              </div>

              {/* 카드 3: 미국 주식 */}
              <div className="rounded-xl shadow-sm text-white p-5 bg-gradient-to-r from-purple-500 to-purple-600">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white/80 text-xs font-medium">미국 주식 (USD)</p>
                  <div className="h-9 w-9 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                    <Globe className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-white/70 text-xs">평가금액</p>
                    <p className="text-xl font-bold leading-tight">{usdFmt(usTotalValue)}</p>
                    <p className="text-white/60 text-xs mt-0.5">{usHoldings.length}종목 | 매입 {usdFmt(usTotalCost)}</p>
                  </div>
                  <div className="w-px h-10 bg-white/20 mx-3" />
                  <div className="text-right">
                    <p className="text-white/70 text-xs">평가손익</p>
                    <p className={`text-xl font-bold leading-tight ${pBg(usTotalPL)}`}>{usTotalPL >= 0 ? '+' : ''}{usdFmt(usTotalPL)}</p>
                    <p className={`text-xs mt-0.5 ${pBg(usTotalPL)}`}>
                      {usTotalCost > 0 ? pctFmt((usTotalPL / usTotalCost) * 100) : '+0.00%'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ══ 1-1. 자동매매 투자한도 사용 현황 ══ */}
            <InvestmentUsageCard
              usage={usage}
              usUsage={usUsage}
              error={usageError}
              showUntracked={showUntracked}
              setShowUntracked={setShowUntracked}
              onSettings={() => navigate('/settings')}
            />

            {/* ══ 2. 예수금 (접이식, 기본 접힘) ══ */}
            <CollapsiblePanel
              defaultOpen={false}
              header={
                <div className="w-full">
                  {/* 1행: 제목 */}
                  <div className="flex items-center space-x-2 mb-2">
                    <Wallet className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="font-semibold text-gray-900">예수금 및 매수가능금액</span>
                  </div>
                  {/* 2행: 요약 숫자 */}
                  <div className="flex items-center space-x-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">D+2 예수금</p>
                      <p className="font-semibold text-gray-800">{krwFmt(d.d2_deposit)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">예수금</p>
                      <p className="font-semibold text-gray-800">{krwFmt(d.withdrawable_cash)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">매수가능</p>
                      <p className="font-semibold text-blue-700">{krwFmt(d.buyable_cash)}</p>
                    </div>
                  </div>
                </div>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6">
                {[
                  { label: 'D+2 예수금',  value: krwFmt(d.d2_deposit),        sub: '정산 예정 금액', bg: 'bg-blue-50 border-blue-100',   tc: 'text-blue-700',   vc: 'text-blue-900',   icon: <CreditCard className="w-4 h-4 text-blue-500" /> },
                  { label: '예수금',       value: krwFmt(d.withdrawable_cash),  sub: '즉시 출금 가능', bg: 'bg-green-50 border-green-100', tc: 'text-green-700', vc: 'text-green-900', icon: <Wallet      className="w-4 h-4 text-green-500" /> },
                  { label: '매수가능금액', value: krwFmt(d.buyable_cash),       sub: '주식 매수 가능', bg: 'bg-purple-50 border-purple-100',tc: 'text-purple-700',vc: 'text-purple-900',icon: <ShoppingCart className="w-4 h-4 text-purple-500" /> },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl p-4 border ${c.bg}`}>
                    <div className="flex items-center justify-between mb-2">{c.icon}<p className={`text-xs font-medium ${c.tc}`}>{c.label}</p></div>
                    <p className={`text-xl font-bold ${c.vc}`}>{c.value}</p>
                    <p className={`text-xs mt-1 ${c.tc}`}>{c.sub}</p>
                  </div>
                ))}
              </div>
            </CollapsiblePanel>

            {/* ══ 3. 미국 주식 보유 (접이식, 기본 펼침) ══ */}
            {usHoldings.length > 0 && (
              <CollapsiblePanel
                defaultOpen={false}
                header={
                  <div className="w-full">
                    {/* 1행: 제목 */}
                    <div className="flex items-center space-x-2 mb-2">
                      <Globe className="w-5 h-5 text-purple-600 shrink-0" />
                      <span className="font-semibold text-gray-900">미국 주식 보유 현황</span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">{usHoldings.length}종목</span>
                    </div>
                    {/* 2행: 요약 숫자 */}
                    <div className="flex items-center space-x-4 text-sm">
                      <div>
                        <p className="text-xs text-gray-400">총 평가</p>
                        <p className="font-semibold text-gray-800">{usdFmt(usTotalValue)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">총 매입</p>
                        <p className="font-semibold text-gray-800">{usdFmt(usTotalCost)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">평가 손익</p>
                        <p className={`font-semibold ${pColor(usTotalPL)}`}>{usTotalPL >= 0 ? '+' : ''}{usdFmt(usTotalPL)}</p>
                      </div>
                    </div>
                  </div>
                }
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['종목','수량','평균매입가','현재가','평가금액','손익'].map(h => (
                          <th key={h} className={`px-5 py-3 text-xs font-medium text-gray-500 uppercase ${h === '종목' ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {usHoldings.map((h, i) => {
                        const curr = h.current_price || h.avg_buy_price;
                        const val  = curr * h.total_quantity;
                        const pl   = (curr - h.avg_buy_price) * h.total_quantity;
                        const plr  = h.avg_buy_price > 0 ? (curr - h.avg_buy_price) / h.avg_buy_price * 100 : 0;
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-5 py-3">
                              <p className="text-sm font-bold text-gray-900">{h.stock_code}</p>
                              <p className="text-xs text-gray-500">{h.stock_name !== h.stock_code ? h.stock_name : h.exchange}</p>
                            </td>
                            <td className="px-5 py-3 text-right text-sm">{h.total_quantity}</td>
                            <td className="px-5 py-3 text-right text-sm">${(h.avg_buy_price || 0).toFixed(2)}</td>
                            <td className="px-5 py-3 text-right text-sm">${curr.toFixed(2)}</td>
                            <td className="px-5 py-3 text-right text-sm font-medium">{usdFmt(val)}</td>
                            <td className={`px-5 py-3 text-right text-sm font-medium ${pColor(pl)}`}>
                              {pl >= 0 ? '+' : ''}{usdFmt(pl)}<br />
                              <span className="text-xs">{pctFmt(plr)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CollapsiblePanel>
            )}

            {/* ══ 4. 국내 보유 종목 (접이식, 기본 펼침) ══ */}
            <CollapsiblePanel
              defaultOpen={false}
              header={
                <div className="w-full">
                  {/* 1행: 제목 */}
                  <div className="flex items-center space-x-2 mb-2">
                    <PieChart className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="font-semibold text-gray-900">국내 보유 종목</span>
                    <span className="flex items-center text-sm text-gray-500"><Eye className="h-4 w-4 mr-1" />{holdings.length}개</span>
                    {selectedStocks.size > 0 && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded flex items-center">
                        <CheckSquare className="h-3 w-3 mr-1" />{selectedStocks.size}개 선택
                      </span>
                    )}
                  </div>
                  {/* 2행: 요약 숫자 */}
                  <div className="flex items-center space-x-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">총 평가</p>
                      <p className="font-semibold text-gray-800">{krwFmt(krTotalValue)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">총 매입</p>
                      <p className="font-semibold text-gray-800">{krwFmt(krPurchase)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">평가 손익</p>
                      <p className={`font-semibold ${pColor(krPL)}`}>{krwFmt(krPL)}</p>
                    </div>
                  </div>
                </div>
              }
            >
              {/* 매도 버튼 행 */}
              {holdings.length > 0 && (
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex justify-end space-x-2">
                  <button onClick={handleSellSelected} disabled={isBulkSelling || !selectedStocks.size}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm shadow-sm ${selectedStocks.size ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'} disabled:opacity-50`}>
                    <ShoppingCart className="h-4 w-4 mr-2" />선택 매도 ({selectedStocks.size})
                  </button>
                  <button onClick={handleBulkSellAll} disabled={isBulkSelling}
                    className="flex items-center px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 shadow-sm">
                    <ShoppingCart className="h-4 w-4 mr-2" />전체 매도
                  </button>
                </div>
              )}

              {holdings.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <PieChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg">보유 중인 국내 종목이 없습니다.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3">
                          <button onClick={handleSelectAll} className="text-gray-500 hover:text-blue-600">
                            {isAllSelected ? <CheckSquare className="h-5 w-5 text-blue-600" /> :
                             isPartialSelected ? <MinusSquare className="h-5 w-5 text-blue-600" /> :
                             <Square className="h-5 w-5" />}
                          </button>
                        </th>
                        {['종목명','보유수량','평균단가','현재가','평가금액','손익','수익률'].map(h => (
                          <th key={h} className={`px-5 py-3 text-xs font-medium text-gray-500 uppercase ${h === '종목명' ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {holdings.map((h, idx) => {
                        const pl   = parseFloat(h.profit_loss || 0);
                        const plr  = parseFloat(h.profit_loss_rate || 0);
                        const isSel = selectedStocks.has(h.stock_code);
                        return (
                          <tr key={`${h.stock_code}-${idx}`}
                            className={`hover:bg-gray-50 cursor-pointer ${isSel ? 'bg-blue-50' : ''}`}
                            onClick={() => handleSelectStock(h.stock_code)}>
                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                              <button onClick={() => handleSelectStock(h.stock_code)}>
                                {isSel ? <CheckSquare className="h-5 w-5 text-blue-600" /> : <Square className="h-5 w-5 text-gray-400 hover:text-blue-600" />}
                              </button>
                            </td>
                            <td className="px-5 py-3">
                              <p className="text-sm font-medium text-gray-900">{h.stock_name}</p>
                              <p className="text-xs text-gray-500">{h.stock_code}</p>
                            </td>
                            <td className="px-5 py-3 text-right text-sm">{parseInt(h.quantity || 0).toLocaleString()}주</td>
                            <td className="px-5 py-3 text-right text-sm">{formatCurrency(h.average_price)}</td>
                            <td className="px-5 py-3 text-right text-sm">{formatCurrency(h.current_price)}</td>
                            <td className="px-5 py-3 text-right text-sm">{formatCurrency(h.evaluation_amount)}</td>
                            <td className={`px-5 py-3 text-right text-sm font-medium ${getChangeColor(pl)}`}>{formatCurrency(pl)}</td>
                            <td className={`px-5 py-3 text-right text-sm font-medium ${getChangeColor(plr)}`}>
                              <div className="flex items-center justify-end">
                                {plr !== 0 && (plr > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />)}
                                {formatPercent(plr)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsiblePanel>

            {/* ══ 5. 계좌 정보 + 하단 요약 ══ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center">
                <CheckCircle className="h-8 w-8 text-blue-500 mr-3 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">계좌번호</p>
                  <p className="text-base font-mono font-semibold text-gray-900">{d.account_number}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center">
                <Activity className="h-8 w-8 text-green-500 mr-3 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">국내 보유 종목 수</p>
                  <p className="text-xl font-bold text-gray-900">{holdings.length}개</p>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center">
                <Coins className="h-8 w-8 text-purple-500 mr-3 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">미국 주식 종목 수</p>
                  <p className="text-xl font-bold text-gray-900">{usHoldings.length}개</p>
                </div>
              </div>
            </div>

            {/* ══ 6. 안내 ══ */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-700">
                  한투 총 자산은 국내·해외 주식과 예수금을 포함한 전체 자산입니다.
                  해외 주식 평가는 당일 환율이 적용됩니다.
                  예수금·매수가능금액은 섹션을 펼쳐 확인하실 수 있습니다.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AccountPage;
